import { useEffect, useLayoutEffect, useReducer } from 'react';
import { Box } from '@nimbus-ds/components';
import {
  SelectionRequest,
  StorePreferenceCreateRequest,
} from '@tiendanube/common';
import { useNavTabs } from 'App/components/Navtabs';
import {
  trackingQuestionnaireContinueClick,
  trackingQuestionnaireReturnClick,
  trackingQuestionnaireSkipClick,
} from 'App/tracking';
import { IonPageStratus, useResponsive } from 'commons/components';
import useStorage from 'commons/hooks/useStorage';
import { useGetStoreInfo } from 'domains/Auth/hooks';
import { AppLoading } from 'domains/PartnersApps/components/EmbeddedApp';
import useTranslationPreOnboarding from 'domains/PreOnboarding/useTranslationPreOnboarding';
import { QuestionnaireForm } from './components';
import { getNextStepIndex } from './getNextStepIndex';
import ImageSrc from './images/new-register-img.png';
import { useGetQuestionnaireOptionsQuery } from './preOnboardingApi';
import formReducer from './questionnaireReducer';
import './QuestionnairePage.scss';

const PRE_ONBOARDING_QUESTIONNAIRE_ANSWERS =
  'pre-onboarding-questionnaire-answers';

interface QuestionnairePageProps {
  onError: () => void;
  onCompleted: (payload: StorePreferenceCreateRequest) => Promise<void>;
}

function removeCountrySuffix(groupKey: string): string {
  return groupKey.replace(/_(ar|br|mx|cl|co)$/, '');
}

function QuestionnairePage({
  onError,
  onCompleted,
}: Readonly<QuestionnairePageProps>) {
  const { id, country } = useGetStoreInfo();
  const [questionnaireCompleted, , completedLoaded] = useStorage(
    `pre-onboarding-questionnaire-completed-${id}`,
    false,
  );
  const {
    data: response,
    isLoading,
    isError,
    isUninitialized,
  } = useGetQuestionnaireOptionsQuery(undefined, {
    skip: questionnaireCompleted === true || !completedLoaded,
  });

  const { isMobile } = useResponsive();
  const t = useTranslationPreOnboarding();
  const questionnaireAnswersKey = `${PRE_ONBOARDING_QUESTIONNAIRE_ANSWERS}-${id}`;
  const [questionnaireAnswers, setQuestionnaireAnswers, loaded] = useStorage(
    questionnaireAnswersKey,
    '',
  );
  const { hideSideBar, hideNavTabs, showNavTabs } = useNavTabs();
  const isQuestionnaireRevamp = country === 'BR' || country === 'MX';

  useLayoutEffect(() => {
    hideSideBar();
    hideNavTabs();
    return () => showNavTabs();
  }, [hideSideBar, hideNavTabs, showNavTabs]);

  const [state, dispatch] = useReducer(formReducer, {
    currentStep: 0,
    answers: {},
    stepHistory: [0],
  });

  useEffect(() => {
    if (!isUninitialized && !isLoading && (!response || isError)) onError();
  }, [response, isError, onError, isUninitialized, isLoading]);

  useEffect(() => {
    if (loaded && questionnaireAnswers) {
      const parsedAnswers = JSON.parse(questionnaireAnswers) as Record<
        string,
        SelectionRequest[]
      >;

      Object.entries(parsedAnswers).forEach(([groupKey, values]) => {
        dispatch({
          type: 'ANSWER_GROUP',
          payload: {
            groupKey,
            values,
          },
        });
      });
    }
  }, [loaded, questionnaireAnswers]);

  if (isLoading || !response || isError || !completedLoaded)
    return <AppLoading />;

  const currentGroup = response.options[state.currentStep];
  const currentValues = state.answers[currentGroup.group] ?? [];
  const isCheckbox = currentGroup.isCheckbox;
  const isLastStep = state.currentStep === response.options.length - 1;

  const clearFollowingAnswers = (startingFrom: string) => {
    const index = response.options.findIndex(
      (step) => step.group === startingFrom,
    );
    const groupsToClear = response.options
      .map((step) => step.group)
      .filter((_, i) => i !== state.currentStep && i >= index);

    groupsToClear.forEach((group) => {
      dispatch({
        type: 'ANSWER_GROUP',
        payload: { groupKey: group, values: [] },
      });
    });
  };

  const setValues = (values: SelectionRequest[]) => {
    const groupKey = currentGroup.group;
    const nextAnswer = values[0];

    const shouldResetAll = groupKey.startsWith('sales_status_') && nextAnswer;

    if (shouldResetAll) {
      clearFollowingAnswers(groupKey);
      dispatch({
        type: 'ANSWER_GROUP',
        payload: { groupKey, values: [nextAnswer] },
      });
    } else {
      dispatch({ type: 'ANSWER_GROUP', payload: { groupKey, values } });
    }
  };

  const handleNextStep = (newValues: SelectionRequest[], fromSkip: boolean) => {
    if (!fromSkip) {
      trackingQuestionnaireContinueClick(
        removeCountrySuffix(currentGroup.group),
      );
    }

    const updatedAnswers = {
      ...state.answers,
      [currentGroup.group]: newValues,
    };

    dispatch({
      type: 'ANSWER_GROUP',
      payload: { groupKey: currentGroup.group, values: newValues },
    });

    const nextStep = getNextStepIndex({
      currentStep: state.currentStep,
      response,
      currentGroup,
      currentValues: newValues,
    });

    setQuestionnaireAnswers(JSON.stringify(updatedAnswers));

    if (nextStep < response.options.length) {
      dispatch({
        type: 'SET_STEP_WITH_HISTORY',
        payload: {
          stepIndex: nextStep,
        },
      });
    } else {
      const payload = Object.values({
        ...state.answers,
        [currentGroup.group]: newValues,
      }).flat();

      onCompleted({ selections: payload, country }).then(() => {
        setQuestionnaireAnswers('');
      });
    }
  };

  const handleSubmit = () => handleNextStep(currentValues, false);

  const handleSkipGroup = (groupKey: string, skipId: number) => {
    clearFollowingAnswers(groupKey);
    trackingQuestionnaireSkipClick(removeCountrySuffix(groupKey));
    handleNextStep([{ domain_mapping_id: skipId }], true);
  };

  const handlePreviousStep = () => {
    if (state.stepHistory.length < 2) return;
    trackingQuestionnaireReturnClick(removeCountrySuffix(currentGroup.group));
    dispatch({ type: 'PREV_STEP' });
  };

  const transformedTypes = currentGroup.types.map(({ id, code }) => ({
    id,
    code: code,
    label: t(`questionnaire.${currentGroup.group}.${code}`),
    description: currentGroup.hasDescriptions
      ? t(`questionnaire.description.${currentGroup.group}.${code}`)
      : undefined,
  }));

  return (
    <IonPageStratus width="large">
      <Box
        display="flex"
        flexDirection="row"
        width="100vw"
        height="100vh"
        overflow="hidden"
        position="absolute"
        top="0"
        left="0"
        backgroundColor="neutral-background"
      >
        {!isMobile && (
          <div
            className={`stratus--questionnaire-page-wrapper ${
              isQuestionnaireRevamp ? 'stratus--no-border' : ''
            }`}
          >
            <img
              src={ImageSrc}
              alt="questionnaire"
              width="100%"
              height="100%"
            />
          </div>
        )}
        <QuestionnaireForm
          values={currentValues}
          setValues={setValues}
          onSubmit={handleSubmit}
          onSkipGroup={handleSkipGroup}
          onReturn={state.currentStep > 0 ? handlePreviousStep : undefined}
          currentGroup={currentGroup.group}
          isCheckbox={isCheckbox}
          questionnaireOptions={transformedTypes}
          isLastStep={isLastStep}
          isQuestionnaireRevamp={isQuestionnaireRevamp}
        />
      </Box>
    </IonPageStratus>
  );
}

export default QuestionnairePage;
